import { i18n } from '@quadralize/i18n'
import { cn, formatDate, formatTime, masks } from '@quadralize/utils'
import { ClockSquare } from '@solar-icons/react'
import * as React from 'react'
import { useTranslation } from 'react-i18next'
import { ScrollArea } from '../../data-display/scroll-area/scroll-area'
import { Popover, PopoverContent, PopoverTrigger } from '../../overlays/popover/popover'
import { Button } from '../../ui/button/button'
import { Input } from '../input/input'
import { LabelWrapper } from '../label/label'

export interface TimePickerProps {
  date?: Date
  setDate?: (date?: Date) => void
  placeholder?: string
  className?: string
  disabled?: boolean
  label?: string
  id?: string
  required?: boolean
  error?: boolean
  editable?: boolean
  helperText?: React.ReactNode
  minTime?: Date
  maxTime?: Date
  onInvalidTime?: () => void
  preserveInvalidInput?: boolean
  minuteStep?: number
}

const HOURS_COUNT = 24
const MINUTES_COUNT = 60
const TIME_MASK_LENGTH = 5
const MAX_TIME_DIGITS = 4
const DEFAULT_MINUTE_STEP = 5

const HOURS = Array.from({ length: HOURS_COUNT }, (_, i) => i)

interface TimeOption {
  value: number
  isDisabled: boolean
  isSelected: boolean
}

export function TimePicker({
  date,
  setDate,
  placeholder,
  className,
  disabled,
  label,
  id,
  required,
  error,
  editable,
  helperText,
  minTime,
  maxTime,
  onInvalidTime,
  preserveInvalidInput = false,
  minuteStep = DEFAULT_MINUTE_STEP,
}: TimePickerProps) {
  const { t } = useTranslation('ui', { i18n })
  const minutes = React.useMemo(
    () => Array.from({ length: Math.ceil(MINUTES_COUNT / minuteStep) }, (_, i) => i * minuteStep),
    [minuteStep],
  )
  const generatedId = React.useId()
  const timePickerId = id || generatedId
  const [internalDate, setInternalDate] = React.useState<Date | undefined>(date)
  const selectedDate = date !== undefined ? date : internalDate

  const [inputValue, setInputValue] = React.useState('')
  const [rangeError, setRangeError] = React.useState<string | null>(null)
  const resolvedPlaceholder = placeholder ?? t('timePicker.placeholder')
  const hoursCleanupRef = React.useRef<AbortController | null>(null)
  const minutesCleanupRef = React.useRef<AbortController | null>(null)

  React.useEffect(() => {
    if (selectedDate) {
      setInputValue(formatTime(selectedDate))
      setRangeError(null)
    } else {
      setInputValue('')
    }
  }, [selectedDate])

  const isOutOfRange = React.useCallback(
    (value: Date): boolean => {
      const valMins = value.getHours() * 60 + value.getMinutes()

      if (minTime) {
        const minMins = minTime.getHours() * 60 + minTime.getMinutes()
        if (valMins < minMins) return true
      }

      if (maxTime) {
        const maxMins = maxTime.getHours() * 60 + maxTime.getMinutes()
        if (valMins > maxMins) return true
      }

      return false
    },
    [minTime, maxTime],
  )

  const getRangeErrorMessage = React.useCallback(
    (value: Date): string | null => {
      const valMins = value.getHours() * 60 + value.getMinutes()

      if (minTime) {
        const minMins = minTime.getHours() * 60 + minTime.getMinutes()
        if (valMins < minMins) return `Horário mínimo: ${formatTime(minTime)}`
      }

      if (maxTime) {
        const maxMins = maxTime.getHours() * 60 + maxTime.getMinutes()
        if (valMins > maxMins) return `Horário máximo: ${formatTime(maxTime)}`
      }

      return null
    },
    [minTime, maxTime],
  )

  const formatTimeInput = React.useCallback(
    (value: string): string => {
      if (!preserveInvalidInput) {
        return masks.time.format(value)
      }

      const digits = value.replace(/\D/g, '').slice(0, MAX_TIME_DIGITS)
      if (digits.length <= 2) {
        return digits
      }

      return `${digits.slice(0, 2)}:${digits.slice(2)}`
    },
    [preserveInvalidInput],
  )

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const maskedValue = formatTimeInput(e.target.value)
    setInputValue(maskedValue)

    if (maskedValue.length === TIME_MASK_LENGTH) {
      const parts = maskedValue.split(':').map(Number)
      if (parts.length === 2) {
        const [hours, minutes] = parts as [number, number]
        if (hours >= 0 && hours < HOURS_COUNT && minutes >= 0 && minutes < MINUTES_COUNT) {
          const baseDate = selectedDate || new Date()
          const newDate = new Date(baseDate)
          newDate.setHours(hours)
          newDate.setMinutes(minutes)
          newDate.setSeconds(0)
          newDate.setMilliseconds(0)

          const errorMsg = getRangeErrorMessage(newDate)
          if (errorMsg) {
            setRangeError(errorMsg)
            setInternalDate(undefined)
            setDate?.(undefined)
            onInvalidTime?.()
            return
          }

          setRangeError(null)
          setInternalDate(newDate)
          setDate?.(newDate)
          return
        }
      }

      onInvalidTime?.()
    } else if (maskedValue === '') {
      setRangeError(null)
      setInternalDate(undefined)
      setDate?.(undefined)
    }
  }

  // Helper to handle time updates
  const handleTimeChange = (type: 'hour' | 'minute', value: number) => {
    const baseDate = selectedDate || new Date()
    const newDate = new Date(baseDate)

    if (type === 'hour') {
      newDate.setHours(value)

      // When selecting an hour, keep current minute when possible.
      // If it's out of range, fallback to the first valid minute of that hour.
      const fallbackMinute = selectedDate?.getMinutes() ?? 0
      newDate.setMinutes(fallbackMinute)

      if (isOutOfRange(newDate)) {
        const firstValidMinute = minutes.find((minute) => {
          const candidate = new Date(newDate)
          candidate.setMinutes(minute)
          candidate.setSeconds(0)
          candidate.setMilliseconds(0)
          return !isOutOfRange(candidate)
        })

        if (firstValidMinute !== undefined) {
          newDate.setMinutes(firstValidMinute)
        }
      }
    } else {
      newDate.setMinutes(value)
    }

    if (!selectedDate) {
      newDate.setSeconds(0)
      newDate.setMilliseconds(0)
    }

    if (isOutOfRange(newDate)) {
      setRangeError(getRangeErrorMessage(newDate))
      onInvalidTime?.()
      return
    }

    setRangeError(null)
    setInternalDate(newDate)
    setDate?.(newDate)
  }

  const isHourDisabled = React.useCallback(
    (hour: number): boolean => {
      const baseDate = selectedDate || new Date()
      const hasAnyAvailableMinute = minutes.some((minute) => {
        const candidate = new Date(baseDate)
        candidate.setHours(hour)
        candidate.setMinutes(minute)
        candidate.setSeconds(0)
        candidate.setMilliseconds(0)

        return !isOutOfRange(candidate)
      })

      return !hasAnyAvailableMinute
    },
    [selectedDate, minutes, isOutOfRange],
  )

  const isMinuteDisabled = React.useCallback(
    (minute: number, hour: number): boolean => {
      const baseDate = selectedDate || new Date()
      const candidate = new Date(baseDate)
      candidate.setHours(hour)
      candidate.setMinutes(minute)
      candidate.setSeconds(0)
      candidate.setMilliseconds(0)

      return isOutOfRange(candidate)
    },
    [selectedDate, isOutOfRange],
  )

  const hourOptions = React.useMemo<TimeOption[]>(
    () =>
      HOURS.map((hour) => ({
        value: hour,
        isDisabled: isHourDisabled(hour),
        isSelected: selectedDate ? selectedDate.getHours() === hour : false,
      })),
    [selectedDate, isHourDisabled],
  )

  const visibleHourOptions = React.useMemo(() => {
    const options = hourOptions.filter((hour) => !hour.isDisabled || hour.isSelected)
    return options.length > 0 ? options : hourOptions
  }, [hourOptions])

  const hiddenDisabledHoursCount = React.useMemo(
    () => hourOptions.length - visibleHourOptions.length,
    [hourOptions, visibleHourOptions],
  )

  const activeHourForMinutes = React.useMemo(() => {
    if (selectedDate) return selectedDate.getHours()

    const firstVisibleHour = visibleHourOptions.find((hour) => !hour.isDisabled)
    return firstVisibleHour?.value ?? 0
  }, [selectedDate, visibleHourOptions])

  const minuteOptions = React.useMemo<TimeOption[]>(
    () =>
      minutes.map((minute) => ({
        value: minute,
        isDisabled: isMinuteDisabled(minute, activeHourForMinutes),
        isSelected: selectedDate ? selectedDate.getMinutes() === minute : false,
      })),
    [minutes, selectedDate, activeHourForMinutes, isMinuteDisabled],
  )

  const visibleMinuteOptions = React.useMemo(() => {
    const options = minuteOptions.filter((minute) => !minute.isDisabled || minute.isSelected)
    return options.length > 0 ? options : minuteOptions
  }, [minuteOptions])

  const hiddenDisabledMinutesCount = React.useMemo(
    () => minuteOptions.length - visibleMinuteOptions.length,
    [minuteOptions, visibleMinuteOptions],
  )

  // Attach non-passive wheel listener via callback ref so it works inside Popover
  const attachWheelListener = React.useCallback(
    (el: HTMLDivElement | null, cleanupRef: React.RefObject<AbortController | null>) => {
      cleanupRef.current?.abort()
      cleanupRef.current = null
      if (!el) return

      const controller = new AbortController()
      cleanupRef.current = controller

      el.addEventListener(
        'wheel',
        (event: WheelEvent) => {
          if (event.deltaY === 0) return

          const viewport = el.querySelector<HTMLElement>('[data-radix-scroll-area-viewport]')
          if (!viewport) return

          const isScrollingDown = event.deltaY > 0
          const atTop = viewport.scrollTop <= 0
          const atBottom = viewport.scrollTop + viewport.clientHeight >= viewport.scrollHeight - 1
          const canScroll = isScrollingDown ? !atBottom : !atTop

          if (!canScroll) return

          event.preventDefault()
          viewport.scrollTop += event.deltaY
        },
        { passive: false, signal: controller.signal },
      )
    },
    [],
  )

  const hoursRefCallback = React.useCallback(
    (el: HTMLDivElement | null) => attachWheelListener(el, hoursCleanupRef),
    [attachWheelListener],
  )

  const minutesRefCallback = React.useCallback(
    (el: HTMLDivElement | null) => attachWheelListener(el, minutesCleanupRef),
    [attachWheelListener],
  )

  const hasRangeError = !!rangeError
  const displayError = error || hasRangeError
  const displayHelperText = rangeError ?? helperText

  return (
    <LabelWrapper
      label={label}
      id={timePickerId}
      required={required}
      error={displayError}
      helperText={displayHelperText}
    >
      <Popover>
        <PopoverTrigger asChild>
          {editable ? (
            <div className="relative">
              <Input
                id={timePickerId}
                placeholder={resolvedPlaceholder}
                value={inputValue}
                onChange={handleInputChange}
                disabled={disabled}
                error={displayError}
                className={cn('pl-9', className)}
                autoComplete="off"
                inputMode="numeric"
                startIcon={<ClockSquare className="h-4 w-4 shrink-0" />}
              />
            </div>
          ) : (
            <Button
              id={timePickerId}
              variant={'outline'}
              size="md"
              justify="start"
              disabled={disabled}
              startIcon={<ClockSquare className="h-4 w-4 shrink-0" />}
              className={cn(
                'w-full font-normal',
                displayError && 'border-error focus:ring-error',
                !selectedDate && 'text-text-tertiary',
                className,
              )}
            >
              <span className="truncate min-w-0">
                {selectedDate ? (
                  formatDate(selectedDate, 'HH:mm')
                ) : (
                  <span>{resolvedPlaceholder}</span>
                )}
              </span>
            </Button>
          )}
        </PopoverTrigger>
        <PopoverContent
          className="w-auto p-0"
          align="start"
          onOpenAutoFocus={editable ? (e) => e.preventDefault() : undefined}
        >
          <div className="flex h-75 w-50">
            <div className="flex-1 flex flex-col border-r border-border-default overflow-hidden">
              <div className="text-center py-2 bg-bg-subtle text-xs font-medium text-text-secondary border-b border-border-default">
                Horas
              </div>
              {hiddenDisabledHoursCount > 0 ? (
                <div className="px-2 py-1 text-[10px] text-text-tertiary text-center border-b border-border-default/60">
                  {hiddenDisabledHoursCount} horário(s) indisponível(is) ocultado(s)
                </div>
              ) : null}
              <ScrollArea ref={hoursRefCallback} className="flex-1 p-2 flex flex-col gap-1">
                {visibleHourOptions.map((hourOption) => {
                  const hour = hourOption.value
                  const isSelected = hourOption.isSelected
                  const isDisabled = hourOption.isDisabled
                  return (
                    <button
                      key={hour}
                      type="button"
                      disabled={isDisabled}
                      onClick={() => handleTimeChange('hour', hour)}
                      className={cn(
                        'w-full text-center py-1.5 rounded-md text-sm transition-colors',
                        isSelected
                          ? 'bg-primary text-primary-fg font-medium'
                          : isDisabled
                            ? 'text-text-tertiary/50 cursor-not-allowed'
                            : 'text-text-primary hover:bg-bg-subtle',
                      )}
                    >
                      {hour.toString().padStart(2, '0')}
                    </button>
                  )
                })}
              </ScrollArea>
            </div>

            <div className="flex-1 flex flex-col overflow-hidden">
              <div className="text-center py-2 bg-bg-subtle text-xs font-medium text-text-secondary border-b border-border-default">
                Minutos
              </div>
              {hiddenDisabledMinutesCount > 0 ? (
                <div className="px-2 py-1 text-[10px] text-text-tertiary text-center border-b border-border-default/60">
                  {hiddenDisabledMinutesCount} minuto(s) indisponível(is) ocultado(s)
                </div>
              ) : null}
              <ScrollArea ref={minutesRefCallback} className="flex-1 p-2 flex flex-col gap-1">
                {visibleMinuteOptions.map((minuteOption) => {
                  const minute = minuteOption.value
                  const isSelected = minuteOption.isSelected
                  const isDisabled = minuteOption.isDisabled
                  return (
                    <button
                      key={minute}
                      type="button"
                      disabled={isDisabled}
                      onClick={() => handleTimeChange('minute', minute)}
                      className={cn(
                        'w-full text-center py-1.5 rounded-md text-sm transition-colors',
                        isSelected
                          ? 'bg-primary text-primary-fg font-medium'
                          : isDisabled
                            ? 'text-text-tertiary/50 cursor-not-allowed'
                            : 'text-text-primary hover:bg-bg-subtle',
                      )}
                    >
                      {minute.toString().padStart(2, '0')}
                    </button>
                  )
                })}
              </ScrollArea>
            </div>
          </div>
        </PopoverContent>
      </Popover>
    </LabelWrapper>
  )
}
